import React from 'react';
import { decode } from 'html-entities';
import {
  filter,
  find,
  findIndex,
  forEach,
  includes,
  isEmpty,
  isNil,
  reduce,
  some,
  split,
} from 'lodash';
import { get, post } from '../../common/httpService';
import translateService from '../../common/translateService';
import productsAvailabilityStatus from '../../components/modules/products/productsAvailabilityStatus';
import Globalizer from '../../globalizer';

const getOriginalDescription = async (url) => {
  return get({ url: `Product/Description?${url}` });
};

const addToCart = (slug, quantitySelected, options, isKit) => {
  return post({
    url: isKit ? `Cart/AddKitToCart` : `Cart/AddProductToCart`,
    body: {
      slug: slug,
      quantity: quantitySelected,
      groupAndOptionIds: options,
    },
  });
};

const addReassNotification = (slug, quantitySelected, options, isKit) => {
  return post({
    url: isKit
      ? `ReassortmentNotification/AddKit`
      : `ReassortmentNotification/AddProduct`,
    body: {
      slug: slug,
      quantity: quantitySelected,
      groupAndOptionIds: options,
    },
  });
};

const getTabsNumber = (data, enableNews) => {
  return (
    // reviews + q&a
    2 +
    (data.description ? 1 : 0) +
    (data.techSpecs && data.techSpecs.length > 0 ? 1 : 0) +
    (data.technologies ? 1 : 0) +
    (data.brandsGuides ? 1 : 0)
    // +
    // (enableNews === 'True' ? 1 : 0)
  );
};

const getColorSelectedFromQueryString = () => {
  const queryStringPairs = split(window.location.search.slice(1), '&');
  const colorPair = find(queryStringPairs, (pair) => includes(pair, 'color='));
  const colorValue = split(colorPair, 'color=')[1]
    ? decodeURIComponent(split(colorPair, 'color=')[1])
    : null;

  return colorValue;
};

const getOptionSelectedFromQueryString = () => {
  const queryStringPairs = split(window.location.search.slice(1), '&');
  const optionPair = find(queryStringPairs, (pair) =>
    includes(pair, 'option=')
  );
  const optionValue = split(optionPair, 'option=')[1]
    ? decodeURI(split(optionPair, 'option=')[1])
    : null;

  return optionValue;
};

const findOption = (options, optionId) => {
  if (isNil(optionId)) return null;

  return find(options, (o) => Number(o.sourceId) === Number(optionId));
};

const findColorFromOption = (foundOption, colorAttributeName) => {
  if (isNil(foundOption) || isNil(colorAttributeName)) {
    return null;
  }

  return !isNil(foundOption.attributes) && !isEmpty(foundOption.attributes)
    ? foundOption.attributes[colorAttributeName]
    : null;
};

const getPreselectionAndModals = (
  attributes,
  isKit,
  foundOption,
  colorFromQueryString
) => {
  let attr = {},
    modals = {};

  if (!isKit) {
    if (!isNil(foundOption)) {
      const optionAttributes = foundOption.attributes;

      forEach(optionAttributes, (val, key) => {
        attr[key] = val;
        modals[key] = false;
      });

      return [attr, modals];
    }
  }

  forEach(attributes, (attribute) => {
    const foundColor = find(
      attribute.values,
      (v) => decode(v.value) === decode(colorFromQueryString)
    );

    attr[attribute.fieldId] =
      attribute.values === null
        ? isKit
          ? String(attribute.kitOptionId)
          : null
        : attribute.values.length === 1
        ? attribute.values[0].value
        : foundColor
        ? foundColor.value
        : null;
    modals[attribute.fieldId] = false;
  });

  return [attr, modals];
};

const getFilteredOptions = (isKit, selection, array, attrName, attrValue) => {
  if (isKit) {
    return find(array, (item) => {
      return decode(item.attributes[attrName]) === decode(String(attrValue));
    });
  }

  let filteredArray = array;

  forEach(selection, (value, key) => {
    filteredArray =
      key === attrName
        ? filter(filteredArray, (item) => {
            return decode(item.attributes[key]) === decode(attrValue);
          })
        : value
        ? filter(filteredArray, (item) => {
            return decode(item.attributes[key]) === decode(value);
          })
        : filteredArray;
  });

  return filteredArray;
};

const getOptionSelected = (isKit, selection, array) => {
  if (isKit) {
    const optionsArray = [];

    if (some(selection, (value) => value === null)) return [];

    forEach(selection, (value, key) => {
      const found = find(
        array,
        (item) => decode(item.attributes[key]) === decode(value)
      );

      if (!isNil(found)) {
        optionsArray.push(found);
      }
    });

    return optionsArray;
  }

  const foundElements = getFilteredOptions(isKit, selection, array);

  return foundElements.length === 1
    ? foundElements[0]
    : foundElements.length === 0
    ? undefined
    : null;
};

const getQtyAvailable = (isKit, selection, array, attrName, attrValue) => {
  let q = 0;

  const found = getFilteredOptions(
    isKit,
    selection,
    array,
    attrName,
    attrValue,
  );

  if (isKit) {
    if (isNil(found)) return 0;
    return !found.isBooking ? found.quantityKit : 0;
  }

  forEach(found, (f) => {
    if (!f.isBooking) {
      q += f.quantity ? f.quantity : 0;
    }
  });

  return q;
};

const getKitQtyAvailable = (options) => {
  if (isNil(options) || isEmpty(options)) return 0;

  const kitUniqueProducts = [];

  forEach(options, (opt) => {
    let uniqueProductIndex = findIndex(
      kitUniqueProducts,
      (p) => p.sourceId === opt.sourceId
    );
    if (uniqueProductIndex === -1) {
      kitUniqueProducts.push({
        sourceId: opt.sourceId,
        quantityKit: opt.quantityKit,
        qtyNeeded: opt.quantityNeeded,
      });
    } else {
      kitUniqueProducts[uniqueProductIndex].qtyNeeded += opt.quantityNeeded;
    }
  });

  const minAvailable = reduce(
    kitUniqueProducts,
    (prev, curr) => {
      return Math.floor(prev.quantityKit / prev.qtyNeeded) >
        Math.floor(curr.quantityKit / curr.qtyNeeded)
        ? curr
        : prev;
    },
    { quantityKit: 10000, qtyNeeded: 1 }
  );

  return minAvailable
    ? Math.floor(minAvailable.quantityKit / minAvailable.qtyNeeded)
    : 0;
};

const isAvailable = (
  isKit,
  selection,
  array,
  attrName,
  attrValue,
  qtySelected,
) => {
  if (isKit) {
    const option = getFilteredOptions(
      isKit,
      selection,
      array,
      attrName,
      attrValue
    );

    return option.quantityKit >= qtySelected || option.bookingOverstock;
  }

  const filteredArray = getFilteredOptions(
    isKit,
    selection,
    array,
    attrName,
    attrValue
  );

  return filteredArray.some(
    (element) => element.quantity >= qtySelected || element.bookingOverstock,
  );
};

const isDiscontinued = (options, isKit) => {
  return isKit
    ? some(options, (o) => o.discontinued)
    : options
    ? options.discontinued
    : false;
};

const isComingSoon = (val, attrName, options, selection, isKit) => {
  const found = getFilteredOptions(isKit, selection, options, attrName, val);

  if (isKit) {
    if (!isNil(found)) {
      return found.isComingSoon;
    }
    return false;
  }

  let comingSoon = false;

  forEach(found, (f) => {
    comingSoon = comingSoon || f.isComingSoon;
  });

  return comingSoon;
};

const isPreorder = (val, attrName, options, selection, isKit) => {
  const found = getFilteredOptions(isKit, selection, options, attrName, val);

  if (isKit) {
    if (!isNil(found)) {
      return found.isPreOrder;
    }
    return false;
  }

  let preorder = false;

  forEach(found, (f) => {
    preorder = preorder || f.isPreOrder;
  });

  return preorder;
};

const isBooking = (val, attrName, options, selection, isKit) => {
  const found = getFilteredOptions(isKit, selection, options, attrName, val);

  if (isKit) {
    if (!isNil(found)) {
      return found.isBooking;
    }
    return false;
  }

  let booking = false;

  forEach(found, (f) => {
    booking = booking || f.isBooking;
  });

  return booking;
};

const isSoonAvailable = (val, attrName, options, selection, isKit) => {
  const found = getFilteredOptions(isKit, selection, options, attrName, val);

  if (isKit) {
    if (!isNil(found)) {
      return (
        (found.isBooking || found.bookingOverstock) &&
        found.bookingOrderDays <= 10
      );
    }
    return false;
  }

  let soonAvailable = false;

  forEach(found, (f) => {
    soonAvailable =
      soonAvailable ||
      ((f.isBooking || f.bookingOverstock) && f.bookingOrderDays <= 10);
  });

  return soonAvailable;
};

const isBookingOverstock = (options, attrName, attrVal, selection, isKit) => {
  const found = getFilteredOptions(
    isKit,
    selection,
    options,
    attrName,
    attrVal
  );

  if (isKit) {
    if (!isNil(found)) {
      return found.bookingOverstock;
    }
    return false;
  }

  let bookingOverstock = false;

  forEach(found, (f) => {
    bookingOverstock = bookingOverstock || f.bookingOverstock;
  });

  return bookingOverstock;
};

const getAvailability = (option) => {
  if (option === null) return '';
  if (option === undefined) return productsAvailabilityStatus.optionUnavailable;
  if (option.isPreOrder) return productsAvailabilityStatus.preorder;
  if (option.isComingSoon) return productsAvailabilityStatus.comingSoon;
  if (option.isBooking)
    return option.bookingOrderDays > 10
      ? productsAvailabilityStatus.booking
      : productsAvailabilityStatus.soonAvailable;
  if (option.discontinued) return productsAvailabilityStatus.discontinued;
  if (option.quantity === 0) {
    if (option.bookingOverstock)
      return option.bookingOrderDays > 10
        ? productsAvailabilityStatus.booking
        : productsAvailabilityStatus.soonAvailable;

    return productsAvailabilityStatus.unavailable;
  }
  if (option.quantity === 1) return productsAvailabilityStatus.oneAvailable;
  return productsAvailabilityStatus.available;
};

const handleBookingOverStock = (options, qtySelected) => {
  const bookingOverstockOptions = filter(options, (o) => o.bookingOverstock);
  const unavailableQtyBookingOverstockOptions = filter(
    bookingOverstockOptions,
    (o) => o.quantityKit < qtySelected
  );

  // caso 1:
  // le opzioni con booking overstock sono disponibili:
  // la qta zero deriva dalle altre -> non disponibile.
  if (
    isNil(unavailableQtyBookingOverstockOptions) ||
    isEmpty(unavailableQtyBookingOverstockOptions)
  )
    return productsAvailabilityStatus.unavailable;

  const unavailableOptions = filter(
    options,
    (o) => !o.bookingOverstock && o.quantityKit < qtySelected
  );

  // caso 2:
  // ho opzioni non disponibili senza booking overstock
  // -> non disponibile
  if (!isNil(unavailableOptions) && !isEmpty(unavailableOptions))
    return productsAvailabilityStatus.unavailable;

  // caso 3:
  // le uniche opzioni non disponibili sono quelle con booking overstock:
  // -> booking
  return productsAvailabilityStatus.booking;
};

const getKitAvailabilityStatus = (qtyAvailable, options, qtySelected) => {
  if (isEmpty(options)) return '';
  let status = {},
    discontinued = [],
    days = 0;

  forEach(options, (o) => {
    status.isPreOrder = status.isPreOrder || o.isPreOrder;
    status.isComingSoon = status.isComingSoon || o.isComingSoon;
    status.isBooking = status.isBooking || o.isBooking;
    status.discontinued = status.discontinued || o.discontinued;
    days = Math.max(days, o.bookingOrderDays);

    if (o.discontinued) {
      discontinued.push(o);
    }
  });

  if (status.discontinued) {
    const minAvailable = getKitQtyAvailable(discontinued);
    if (qtySelected > minAvailable) {
      return minAvailable === 0
        ? productsAvailabilityStatus.discontinued
        : productsAvailabilityStatus.qtyDiscontinued;
    }
  }
  if (qtyAvailable < qtySelected && some(options, (o) => o.bookingOverstock))
    return handleBookingOverStock(options, qtySelected);

  if (qtyAvailable === 0) return productsAvailabilityStatus.unavailable;
  if (status.isComingSoon) return productsAvailabilityStatus.comingSoon;
  if (status.isPreOrder) return productsAvailabilityStatus.preorder;
  if (status.isBooking)
    return days > 10
      ? productsAvailabilityStatus.booking
      : productsAvailabilityStatus.soonAvailable;

  return qtySelected <= qtyAvailable
    ? qtyAvailable === 1
      ? productsAvailabilityStatus.oneAvailable
      : productsAvailabilityStatus.available
    : productsAvailabilityStatus.qtyUnavailable;
};

const getAvailabilityStatus = (qtyAvailable, option, qtySelected) => {
  const availabilityStatus = getAvailability(option);
  if (
    availabilityStatus === '' ||
    availabilityStatus === productsAvailabilityStatus.comingSoon ||
    availabilityStatus === productsAvailabilityStatus.booking ||
    availabilityStatus === productsAvailabilityStatus.optionUnavailable ||
    availabilityStatus === productsAvailabilityStatus.unavailable ||
    availabilityStatus === productsAvailabilityStatus.soonAvailable
  )
    return availabilityStatus;

  if (availabilityStatus === productsAvailabilityStatus.preorder) {
    if (option.discontinued) {
      return qtySelected > qtyAvailable
        ? qtyAvailable === 0
          ? productsAvailabilityStatus.discontinued
          : productsAvailabilityStatus.qtyDiscontinued
        : availabilityStatus;
    }
    return qtySelected <= qtyAvailable
      ? availabilityStatus
      : productsAvailabilityStatus.qtyUnavailable;
  }

  if (availabilityStatus === productsAvailabilityStatus.discontinued) {
    return qtySelected > qtyAvailable
      ? qtyAvailable === 0
        ? availabilityStatus
        : productsAvailabilityStatus.qtyDiscontinued
      : qtyAvailable === 1
      ? productsAvailabilityStatus.oneAvailable
      : productsAvailabilityStatus.available;
  }

  if (qtySelected <= qtyAvailable) {
    return qtyAvailable === 1
      ? productsAvailabilityStatus.oneAvailable
      : productsAvailabilityStatus.available;
  }

  return option.bookingOverstock
    ? option.bookingOrderDays > 10
      ? productsAvailabilityStatus.booking
      : productsAvailabilityStatus.soonAvailable
    : productsAvailabilityStatus.qtyUnavailable;
};

const getBookingDays = (option, isKit) => {
  if (isKit && !isEmpty(option)) {
    const found = filter(option, (o) => o.isBooking || o.bookingOverstock);

    const days = reduce(
      found,
      (curr, next) => {
        return Math.max(curr, next.bookingOrderDays);
      },
      0
    );

    return isNil(days) ? '' : days;
  }

  return isNil(option) || isEmpty(option) ? '' : option.bookingOrderDays;
};

const getPreorderDate = (option, isKit) => {
  if (isKit && !isEmpty(option)) {
    const found = find(option, (o) => o.isPreOrder);

    return found ? found.preOrderArrivalDate : '';
  }

  return isNil(option) || isEmpty(option) ? '' : option.preOrderArrivalDate;
};

const getComingSoonDate = (option, isKit) => {
  if (isKit && !isEmpty(option)) {
    const found = find(option, (o) => o.isComingSoon);

    return found ? found.comingSoonArrivalDate : '';
  }

  return isNil(option) || isEmpty(option) ? '' : option.comingSoonArrivalDate;
};

const getSelectedAttributes = (selection) => {
  let s = '';

  forEach(selection, (value) => {
    if (!isNil(value)) {
      s += value;
      s += ', ';
    }
  });

  s = s.slice(0, s.length - 2);
  // s += '.';

  return s;
};

const isFastShipping = (minutesToTodayShipping) => {
  return minutesToTodayShipping > 0;
};

const getDeliveryMessage = (now) => {
  return get({
    url: `product/DeliveryMessage?utcTime=${now.toISOString()}`,
  });
};

const getFastShippingMessage = (h, m) => {
  const hours = h === 1 ? 'hour' : 'hours';
  const minutes = m === 1 ? 'minute' : 'minutes';

  return translateService.interpolate(
    h === 0
      ? `productDetail.fastShippingMM`
      : m === 0
      ? `productDetail.fastShippingHH`
      : `productDetail.fastShippingHHMM`,
    {
      hh: `${h} ${translateService.get(`productDetail.time.${hours}`)}`,
      mm: `${m} ${translateService.get(`productDetail.time.${minutes}`)}`,
    }
  );
};

const getPriceGroup = (priceObj, qtySelected) => {
  let g = null;

  for (let i = 0; i < 4; i++) {
    if (qtySelected >= priceObj[`price${i}QtaMin`]) {
      if (priceObj[`price${i}QtaMax`] <= 0) {
        g = i;
        break;
      }

      if (qtySelected <= priceObj[`price${i}QtaMax`]) {
        g = i;
        break;
      }
    }
  }
  return g;
};

const getPrice = (isKit, options, basePrice, qtySelected) => {
  if (
    isNil(qtySelected) ||
    qtySelected < 1 ||
    isNil(options) ||
    isEmpty(options)
  )
    return undefined;

  if (isKit) {
    if (isNil(basePrice)) return undefined;
    const g = getPriceGroup(basePrice, qtySelected);
    let p = basePrice[`price${g}`];

    forEach(options, (o) => {
      p += o.priceVariation;
    });

    return p;
  }

  const g = getPriceGroup(options.price, qtySelected);
  return options.price[`price${g}`];
};

const calculateDiscount = (price, retailPrice) => {
  const discount = Math.floor(100 - (price * 100) / retailPrice);
  return discount > 0 ? discount : 0;
};

const getFormattedDiscount = (discount) => {
  return discount
    ? Math.floor(discount) === 0
      ? ''
      : `-${Math.floor(discount)}%`
    : '';
};

const getFormattedPrice = (price, decimalPlaces) => {
  Globalizer.init();
  return !isNil(price) ? Globalizer.formatNumber(price, decimalPlaces) : '';
};

const shouldShowDiscountDescriptionContainer = (tags) =>
  some(tags, (t) => !isNil(t.descriptionText) && !isEmpty(t.descriptionText));

const getProductsBySlugs = async (products) => {
  const res = await post({
    url: `ProductBrowse/GetProductsBySlugsWithColor`,
    body: products.slice(0, 30),
  });
  return reduce(
    res,
    (acc, p) => {
      let prd = find(products, (item) => includes(item.slug, p.slug));
      if (
        !isNil(prd) &&
        !isNil(prd.alternativeImage) &&
        !isEmpty(prd.alternativeImage)
      ) {
        p.imageSrc = prd.alternativeImage;
      }
      acc.push(p);
      return acc;
    },
    [],
  );
};

const _getAvailabilityText = (
  qtyAvailable,
  qtySelected,
  isBooking,
  bookingOverstock,
  comingSoon,
  preorder,
) => {
  if (qtyAvailable === 0 || qtyAvailable < qtySelected) {
    if (bookingOverstock | isBooking)
      return translateService.get(`productDetail.availability.booking`);
    if (comingSoon)
      return translateService.get(`productDetail.availability.comingSoon`);
    if (preorder)
      return translateService.get(`productDetail.availability.preorder`);
    return translateService.get(`productDetail.availability.unavailable`);
  }

  if (qtyAvailable >= qtySelected) {
    if (qtyAvailable === 1)
      return translateService.get(`productDetail.availability.oneAvailable`);
    if (qtyAvailable > 1)
      return translateService.get(`productDetail.availability.available`);
  }

  if (isBooking)
    return translateService.get(`productDetail.availability.booking`);

  return '';
};

const _getCssClassAvailability = (
  qtyAvailable,
  qtySelected,
  isBooking,
  bookingOverstock,
  soonAvailable,
  preorder,
) => {
  if (qtyAvailable === 0 || qtyAvailable < qtySelected) {
    if (bookingOverstock | isBooking)
      return soonAvailable ? 'available' : 'booking';

    if (preorder) return 'available';
    return 'unavailable';
  }
  if (qtyAvailable >= 1 || qtyAvailable >= qtySelected) return 'available';

  if (isBooking) return soonAvailable ? 'available' : 'booking';

  return '';
};

const getAvailabilityLabel = (
  isKit,
  selection,
  options,
  attrName,
  attrValue,
  qtySelected,
) => {
  const qtyAvailable = getQtyAvailable(
    isKit,
    selection,
    options,
    attrName,
    attrValue,
  );

  const booking = isBooking(attrValue, attrName, options, selection, isKit);
  const soonAvailable = isSoonAvailable(
    attrValue,
    attrName,
    options,
    selection,
    isKit,
  );
  const bookingOverstock = isBookingOverstock(
    options,
    attrName,
    attrValue,
    selection,
    isKit,
  );
  const comingSoon = isComingSoon(
    attrValue,
    attrName,
    options,
    selection,
    isKit,
  );
  const preorder = isPreorder(attrValue, attrName, options, selection, isKit);

  const t = _getAvailabilityText(
    qtyAvailable,
    qtySelected,
    booking,
    bookingOverstock,
    comingSoon,
    preorder,
  );
  return (
    <span
      className={`availability ${_getCssClassAvailability(
        qtyAvailable,
        qtySelected,
        booking,
        bookingOverstock,
        soonAvailable,
        preorder,
      )}`}
    >
      {t}
    </span>
  );
};

const getAvailabilityCssClass = (
  isKit,
  selection,
  options,
  attrName,
  attrValue,
  qtySelected,
) => {
  const qtyAvailable = getQtyAvailable(
    isKit,
    selection,
    options,
    attrName,
    attrValue,
  );

  const booking = isBooking(attrValue, attrName, options, selection, isKit);
  const soonAvailable = isSoonAvailable(
    attrValue,
    attrName,
    options,
    selection,
    isKit,
  );
  const bookingOverstock = isBookingOverstock(
    options,
    attrName,
    attrValue,
    selection,
    isKit,
  );
  const preorder = isPreorder(attrValue, attrName, options, selection, isKit);

  return _getCssClassAvailability(
    qtyAvailable,
    qtySelected,
    booking,
    bookingOverstock,
    soonAvailable,
    preorder,
  );
};

export {
  getOriginalDescription,
  addToCart,
  addReassNotification,
  getTabsNumber,
  getColorSelectedFromQueryString,
  getOptionSelectedFromQueryString,
  findOption,
  findColorFromOption,
  getPreselectionAndModals,
  getFilteredOptions,
  getOptionSelected,
  getQtyAvailable,
  getKitQtyAvailable,
  isAvailable,
  isDiscontinued,
  isComingSoon,
  isPreorder,
  isBooking,
  isSoonAvailable,
  isBookingOverstock,
  getAvailability,
  handleBookingOverStock,
  getKitAvailabilityStatus,
  getAvailabilityStatus,
  getBookingDays,
  getPreorderDate,
  getComingSoonDate,
  getSelectedAttributes,
  isFastShipping,
  getDeliveryMessage,
  getFastShippingMessage,
  getPriceGroup,
  getPrice,
  getFormattedPrice,
  calculateDiscount,
  getFormattedDiscount,
  shouldShowDiscountDescriptionContainer,
  getProductsBySlugs,
  getAvailabilityLabel,
  getAvailabilityCssClass,
};
